import { logger } from './logger';
import { APP_SOURCE, MESSAGE_TYPES, SDK_SOURCE } from '../types/message';

/**
 * 메시지 핸들러 함수 타입
 * payload를 받아서 처리하는 함수
 */
type MessageHandler = (payload?: unknown) => void;

const APP_MESSAGE_TYPES = new Set<string>([
  MESSAGE_TYPES.APP_READY,
  MESSAGE_TYPES.APP_CLOSE_REQUEST,
  MESSAGE_TYPES.APP_PONG,
  MESSAGE_TYPES.APP_CHAT_ID_CHANGED,
  MESSAGE_TYPES.APP_UNREAD_REFRESH_REQUIRED,
  MESSAGE_TYPES.APP_CHAT_STATUS,
  MESSAGE_TYPES.APP_OPEN_URL,
  MESSAGE_TYPES.APP_SESSION_REQUIRED,
  MESSAGE_TYPES.APP_CHAT_RESET_REQUEST,
  MESSAGE_TYPES.APP_DELIVERY_ADDRESS_REQUEST,
]);

/**
 * SDK ↔ chat-app 간 postMessage 통신 관리
 *
 * 사용 흐름:
 * 1. connect(iframe, origin) - iframe과 연결
 * 2. subscribe('APP_READY', handler) - 메시지 구독
 * 3. send('SDK_INIT', { theme: 'light' }) - 메시지 전송
 * 4. destroy() - 정리
 */
class MessageBridgeClass {
  /** chat-app이 로드된 iframe 참조 */
  private iframe: HTMLIFrameElement | null = null;

  /** chat-app의 origin (보안 검증용) */
  private targetOrigin: string = '';

  /** 메시지 타입별 핸들러 저장소 */
  private handlers: Map<string, Set<MessageHandler>> = new Map();

  /** window에 등록한 이벤트 핸들러 (정리용) */
  private boundMessageHandler: ((event: MessageEvent) => void) | null = null;

  /** 연결 상태 */
  private isConnected: boolean = false;

  /**
   * 연결 상태 확인
   * @returns 연결되어 있으면 true
   */
  getIsConnected(): boolean {
    return this.isConnected;
  }

  /**
   * iframe과 연결하고 메시지 리스너 등록
   *
   * @param iframe - chat-app이 로드된 iframe 요소
   * @param targetOrigin - chat-app의 origin (예: 'https://chat.aicx.kr')
   *
   * @example
   * messageBridge.connect(iframeElement, 'https://chat.aicx.kr');
   */
  connect(iframe: HTMLIFrameElement, targetOrigin: string): void {
    // 이미 연결되어 있으면 무시
    if (this.isConnected) {
      logger.warn('MessageBridge가 이미 연결되어 있습니다.');
      return;
    }

    let parsedOrigin: string;
    try {
      parsedOrigin = new URL(targetOrigin).origin;
    } catch {
      throw new Error('[AICX SDK] 유효한 targetOrigin이 필요합니다.');
    }

    if (targetOrigin === '*' || parsedOrigin !== targetOrigin) {
      throw new Error('[AICX SDK] targetOrigin은 정확한 origin이어야 합니다.');
    }

    this.iframe = iframe;
    this.targetOrigin = targetOrigin;

    // 메시지 수신 핸들러를 bind해서 저장 (나중에 removeEventListener 하려면 같은 참조가 필요)
    this.boundMessageHandler = this.handleMessage.bind(this);

    // window에서 message 이벤트 수신 시작
    window.addEventListener('message', this.boundMessageHandler);
    this.isConnected = true;

    logger.log('MessageBridge 연결됨', { targetOrigin });
  }

  /**
   * chat-app으로 메시지 전송
   *
   * @param type - 메시지 타입 (예: 'SDK_INIT')
   * @param payload - 전달할 데이터 (optional)
   *
   * @example
   * messageBridge.send('SDK_INIT', { theme: 'dark', debug: true });
   * messageBridge.send('SDK_PING'); // payload 없이도 가능
   */
  send(type: string, payload?: unknown): void {
    // iframe이 없으면 전송 불가
    if (!this.iframe?.contentWindow) {
      logger.warn('iframe이 없어서 메시지를 보낼 수 없습니다.');
      return;
    }

    // origin이 설정 안 됐으면 전송 불가
    if (!this.targetOrigin) {
      logger.warn('targetOrigin이 설정되지 않았습니다.');
      return;
    }

    // 메시지 객체 생성
    const message = {
      type,
      payload,
      source: SDK_SOURCE, // 'aicx-sdk' - chat-app이 이걸로 SDK 메시지인지 판단
    };

    // iframe 안의 chat-app으로 메시지 전송
    // targetOrigin을 명시해서 다른 곳으로 메시지가 가는 것을 방지 (보안)
    this.iframe.contentWindow.postMessage(message, this.targetOrigin);
    logger.log('메시지 전송', { type, source: SDK_SOURCE });
  }

  /**
   * 특정 메시지 타입 구독
   * chat-app에서 해당 타입의 메시지가 오면 handler 실행
   *
   * @param type - 구독할 메시지 타입 (예: 'APP_READY')
   * @param handler - 메시지 수신 시 실행할 함수
   * @returns 구독 해제 함수
   *
   * @example
   * const unsubscribe = messageBridge.subscribe('APP_READY', () => {
   *   console.log('chat-app 준비 완료!');
   * });
   *
   * // 나중에 구독 해제
   * unsubscribe();
   */
  subscribe(type: string, handler: MessageHandler): () => void {
    // 해당 타입의 핸들러 Set이 없으면 생성
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }

    // 핸들러 등록
    this.handlers.get(type)!.add(handler);
    logger.log('메시지 구독 등록', { type });

    // 구독 해제 함수 반환 (클로저로 type과 handler 캡처)
    return () => {
      this.handlers.get(type)?.delete(handler);
      logger.log('메시지 구독 해제', { type });
    };
  }

  /**
   * window의 message 이벤트 핸들러
   * chat-app에서 postMessage로 보낸 메시지를 여기서 수신
   */
  private handleMessage(event: MessageEvent): void {
    // 🔒 보안 검증 1: origin 체크
    // 설정된 targetOrigin이 아닌 곳에서 온 메시지는 무시
    // 악성 사이트가 보낸 메시지를 차단!
    if (event.origin !== this.targetOrigin) {
      return;
    }

    // 🔒 보안 검증 2: 실제 iframe window 체크
    if (!this.iframe?.contentWindow || event.source !== this.iframe.contentWindow) {
      return;
    }

    if (!event.data || typeof event.data !== 'object') {
      return;
    }

    const { type, payload, source } = event.data as {
      type?: unknown;
      payload?: unknown;
      source?: unknown;
    };

    if (typeof type !== 'string' || !APP_MESSAGE_TYPES.has(type)) {
      return;
    }

    // 🔒 보안 검증 3: message source 식별자 체크
    // 우리 chat-app에서 보낸 메시지만 처리
    // 다른 라이브러리(구글 애널리틱스 등)의 postMessage 무시
    if (source !== APP_SOURCE) {
      return;
    }

    logger.log('메시지 수신', { type, source: APP_SOURCE });

    // 등록된 핸들러 실행
    const handlers = this.handlers.get(type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(payload);
        } catch (error) {
          logger.error('메시지 핸들러 실행 중 에러', error);
        }
      });
    }
  }

  /**
   * MessageBridge 정리
   * SDK destroy() 시 호출해서 메모리 누수 방지
   */
  destroy(): void {
    // window에서 이벤트 리스너 제거
    if (this.boundMessageHandler) {
      window.removeEventListener('message', this.boundMessageHandler);
    }

    // 모든 핸들러 정리
    this.handlers.clear();

    // 참조 정리
    this.iframe = null;
    this.targetOrigin = '';
    this.boundMessageHandler = null;
    this.isConnected = false;

    logger.log('MessageBridge 정리 완료');
  }
}

/**
 * 싱글톤 인스턴스
 * 앱 전체에서 하나의 MessageBridge만 사용
 */
export const messageBridge = new MessageBridgeClass();
